/**
 * Class to provide dynamic date string functionality.
 */
export class DateStringCreator {
  /**
   * Helper function to calculate the difference in days between two dates
   * @param {Date} date1 - The first date
   * @param {Date} date2 - The second date
   * @returns {number} The difference in days
   */
  static _getDaysDifference(date1: Date, date2: Date) {
    // Get time difference in milliseconds
    const diffTime = Math.abs(date2.getTime() - date1.getTime());
    // Convert to days and return
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Function to create a human readable date in the form 'today' / 'x d ago' from an ISO date.
   * @param publicationDateIso String containing ISO date (e.g. 2019-09-15T00:00:00.000+02:00)
   * @param labelToday String label to be used for the date string
   * @param labelDaysAgo String label to be used for the date string
   * @returns String containing either the labelToday parameter if publicationDateIso corresponds to the current day
   * or a String formed from the labelDaysAgo parameter if publicationDateIso lies within 1 to 3 days in the past from the current day
   * (e.g. 2 d ago). In all other cases, the empty string is returned.
   */
  static createDateFromLabels(
    publicationDateIso: string,
    labelToday: string,
    labelDaysAgo: string,
  ) {
    let dateString = "";

    if (publicationDateIso) {
      const now = new Date();
      const publicationDate = new Date(publicationDateIso);

      // Reset hours to compare just the dates
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const pubDate = new Date(
        publicationDate.getFullYear(),
        publicationDate.getMonth(),
        publicationDate.getDate(),
      );

      const daysDifference = this._getDaysDifference(pubDate, today);

      // Check if dates are the same day (less than 1 day apart)
      if (today.getTime() === pubDate.getTime() && labelToday) {
        dateString = labelToday;
      } else if (today > pubDate && daysDifference <= 3 && labelDaysAgo) {
        dateString = labelDaysAgo.replace(
          "$numDays",
          daysDifference.toString(),
        );
      }
    }

    return dateString;
  }
}
